/* eslint-disable max-lines-per-function */
import { ApiRoutes } from './apiRoutes'

import { useApiHandler } from '@/hooks/useApiHandler'
import { SapAliado, SapCategoria, SapOptions } from '@/types'
import { ApiConfig, ApiResponse } from '@/types/api'
import {
    Discount,
    DiscountDto,
    EarlyPaymentDiscount,
    InformationTexts
} from '@/types/discounts'
import {
    Modality,
    OptionsField,
    PlanProgram,
    InstitutionSap
} from '@/types/forms/'
import { CreateAcademicOfferingPayload } from '@/types/forms/CreateAcademicOfferingPayload'
import { AcademicCycleWithInitialDate } from '@/types/forms/academicAdmission'
import { IcfesTypesResponse, IcfesFormFieldsResponse } from '@/types/icfes'

export function useResourcesApi(config?: Partial<ApiConfig>): any {
    const api = useApiHandler(ApiRoutes.RESOURCES, config)

    return {
        getEpsList: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-eps-list'),

        getBusinessSector: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-business-sector'),

        getEconomicActivity: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-economic-activity'),

        getEnterpriseType: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-enterprise-type'),

        getPositionLevel: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-position-level'),

        getBloodTypes: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-blood-type'),

        getSapTalentType: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-talent-type'),

        getGenders: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('genders/all'),

        getRegions: (countryId: string): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>(`sap-region?countryId=${countryId}`),

        getCities: (
            countryId: string,
            departmentId: string
        ): Promise<ApiResponse<SapOptions[]>> => {
            const url = `sap-city?countryId=${countryId}&regionId=${departmentId}`

            return api
                .get<SapOptions[]>(url)
                .then((response) => {
                    return response
                })
                .catch((error) => {
                    console.error('Error fetching cities:', error)
                    throw error
                })
        },

        getSchoolsByName: (
            search: string
        ): Promise<ApiResponse<InstitutionSap[]>> =>
            api.get<InstitutionSap[]>(
                `/ed-institutions/denominacion-objeto?denominacionObjeto=${search}`
            ),

        getSchoolById: (id: number): Promise<ApiResponse<InstitutionSap>> =>
            api.get<InstitutionSap>(`/ed-institutions/${id}`),

        getUniversitiesByName: (
            search: string
        ): Promise<ApiResponse<InstitutionSap[]>> =>
            api.get<InstitutionSap[]>(
                `/api/v1/ed-superior-institutions/alias?alias=${search}`
            ),

        getUniversityById: (id: number): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>(`/api/v1/ed-superior-institutions/${id}`),

        getDocumentTypes: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-document-type'),

        getEducationalLevels: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-education-level-list'),

        getBachillerTitles: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-titulo-bachiller'),

        getSapUniversityTitles: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-titulo-universitario'),

        getStudyStatus: (): Promise<ApiResponse<any[]>> =>
            api.get<any[]>('/constantes?ids=7&ids=8'),

        getMaritalStatus: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-marital-status-list'),

        getSocioeconomicStatus: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-socieconomic-statrum'),

        getResidenceZone: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-residence-zone'),

        getRelationshipList: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-relationship-list'),

        getNationalityList: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-nationality-list'),

        getLocality: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-locality'),

        getResidenceTime: (): Promise<ApiResponse<any[]>> =>
            api.get<any[]>('/sap-bogota-time'),

        getLivingType: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-living-type'),

        getForeingExamList: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-foreing-exam-list'),

        getEthnicityList: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-ethnicity'),

        getDisabilityList: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-disability-type'),

        getCountries: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-country-list'),

        getDepartments: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-department'),

        getAcademicPrograms: (
            category?: string
        ): Promise<ApiResponse<PlanProgram[]>> =>
            api.get<PlanProgram[]>(
                category
                    ? `/academic-plan/all?category=${encodeURIComponent(category)}`
                    : '/academic-plan/all'
            ),

        getActivePrograms: (): Promise<ApiResponse<PlanProgram[]>> =>
            api.get<PlanProgram[]>('/academic-plan/active-programs'),

        getAdmissionTypesByProgramId: (
            programId: string
        ): Promise<ApiResponse<OptionsField[]>> =>
            api.get<OptionsField[]>(`/admission-types/${programId}`),

        getCampus: (
            programId: string,
            admissionTypeId: string
        ): Promise<ApiResponse<OptionsField[]>> =>
            api.get<OptionsField[]>(
                `/headquarter/${programId}?admissionTypeId=${admissionTypeId}`
            ),

        getJornada: (
            programId: string,
            admissionTypeId: string
        ): Promise<ApiResponse<OptionsField[]>> =>
            api.get<OptionsField[]>(
                `/jornada/${programId}?admissionTypeId=${admissionTypeId}`
            ),

        getAcademicCycle: (
            programId: string,
            admissionTypeId: string,
            jornada: string
        ): Promise<ApiResponse<OptionsField[]>> =>
            api.get<OptionsField[]>(
                `/academic-cycles/${programId}?admissionTypeId=${admissionTypeId}&jornadaId=${jornada}`
            ),

        getAcademicModality: (
            programId: string,
            admissionTypeId: string,
            jornada: string
        ): Promise<ApiResponse<Modality>> =>
            api.get<Modality>(
                `/academic-modalities/${programId}?admissionTypeId=${admissionTypeId}&jornadaId=${jornada}`
            ),

        getAcademicCycleWithInitialDate: (
            programId: number,
            claseOyente: number,
            headquarterId: number,
            jornadaId: number
        ): Promise<ApiResponse<AcademicCycleWithInitialDate[]>> =>
            api.get<AcademicCycleWithInitialDate[]>(
                `/academic-offerings/dates/${programId}?claseOyente=${claseOyente}&headquarterId=${headquarterId}&jornadaId=${jornadaId}`
            ),

        getAcademicModalityCategories: (): Promise<ApiResponse<string[]>> =>
            api.get<string[]>('/academic-modalities/categories'),

        getAcademicModalities: (): Promise<ApiResponse<any[]>> =>
            api.get<any[]>('/academic-offerings/modalities'),

        // Endpoint for inscription preload
        getInscriptionPreload: ({
            codPrograma,
            modalidad
        }: {
            codPrograma: string
            modalidad: string
        }): Promise<ApiResponse<any>> =>
            api.get<any>('/inscription-preload/preload', {
                params: { codPrograma, modalidad }
            }),

        getDiscounts: (
            idInscription: string,
            recognizesEthnicGroup: boolean
        ): Promise<ApiResponse<Discount[]>> =>
            api.get<Discount[]>(
                `/sap-type-discounts?documentType=D&idInscription=${idInscription}&recognizesEthnicGroup=${recognizesEthnicGroup}`
            ),

        getDiscount: (codDiscount: string): Promise<ApiResponse<DiscountDto>> =>
            api.get<DiscountDto>(`/discount/${codDiscount}`),

        getInformationTexts: (): Promise<ApiResponse<InformationTexts>> =>
            api.get<InformationTexts>('/discount/informative-texts'),
        getEarlyPayment: (): Promise<ApiResponse<EarlyPaymentDiscount>> =>
            api.get<EarlyPaymentDiscount>(
                '/early-payment-discount/latest-active-date'
            ),

        // Address Catalog endpoints
        getSapRazonList: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-razon-list'),

        getSapMedioList: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-medio-list'),

        getSapCavList: (): Promise<ApiResponse<SapOptions[]>> =>
            api.get<SapOptions[]>('/sap-cav-list'),

        getStreetTypes: (): Promise<ApiResponse<any[]>> =>
            api.get<any[]>('/address-catalog/street-types'),

        getStreetQualifiers: (): Promise<ApiResponse<any[]>> =>
            api.get<any[]>('/address-catalog/street-qualifiers'),

        getStreetOrientations: (): Promise<ApiResponse<any[]>> =>
            api.get<any[]>('/address-catalog/street-orientations'),

        // ICFES Integration endpoints
        getIcfesTypes: (): Promise<ApiResponse<IcfesTypesResponse>> =>
            api.get<IcfesTypesResponse>('/icfes-integrations/types'),

        getIcfesFormFields: (
            tipoIcfes: string
        ): Promise<ApiResponse<IcfesFormFieldsResponse>> =>
            api.get<IcfesFormFieldsResponse>(
                `/icfes-integrations/form-fields/${tipoIcfes}`
            ),
        
        getAliados: (): Promise<ApiResponse<SapAliado[]>> =>
                api.get<SapAliado[]>('/sap-aliado-list'),
    
        getCategoriasAdmision: (): Promise<ApiResponse<SapCategoria[]>> =>
                api.get<SapCategoria[]>('/sap-categoria-list'),

        getSapClaseOyenteByProgramId: (
            programId: string
        ): Promise<ApiResponse<OptionsField[]>> =>
            api.get<OptionsField[]>(`/sap-clase-oyente?idPlan=${programId}`),

        getSapCampusByProgramId: (
            programId: string,
            tipoIngreso: string
        ): Promise<ApiResponse<OptionsField[]>> =>
            api.get<OptionsField[]>(
                `/sap-campus?idPlan=${programId}&tipoIngreso=${tipoIngreso}`
            ),

        getSapJornadaByProgramId: (
            programId: string,
            tipoIngreso: string,
            campusId: string
        ): Promise<ApiResponse<OptionsField[]>> =>
            api.get<OptionsField[]>(
                `/sap-jornada?idPlan=${programId}&tipoIngreso=${tipoIngreso}&campus=${campusId}`
            ),

        getSapPeriodosInicioByProgramId: (
            programId: string,
            admissionTypeId: string,
            campusId: string,
            jornadaId: string
        ): Promise<ApiResponse<OptionsField[]>> =>
            api.get<OptionsField[]>(
                `/sap-period?idPlan=${programId}&tipoIngreso=${admissionTypeId}&campus=${campusId}&jornada=${jornadaId}`
            ),

        getAcademicPlansByModality: (
            modalityId: string
        ): Promise<ApiResponse<PlanProgram[]>> =>
            api.get<PlanProgram[]>(
                `/academic-plan/programs/active/by-modalities-final/${modalityId}`
            ),

        createOrGetAcademicOfferingId: (
            payload: CreateAcademicOfferingPayload
        ): Promise<ApiResponse<{ id: number }>> =>
            api.post<{ id: number }>('/academic-offerings/create', payload),

        // Obtener URL del instructivo de ingreso periodo diferente desde S3
        getInstructiveDifferentPeriodUrl: (): Promise<ApiResponse<string>> =>
            api.get<string>('/documents/instructive-different-period-url'),

        getAllDataAcademicOfferings: (id: number): Promise<ApiResponse<any>> =>
            api.get<any>(`/academic-offerings/${id}/all-data`)
    }
}
